import React, { useEffect, useMemo, useState } from "react";
import { http, streamSSE } from "../lib/api";
import MarkdownLite from "../components/MarkdownLite";
import { applyCandidateFilters, filtersActive, filterSummary } from "../lib/filters";
import { PaperPlaneRight, Envelope, ChatText, X, Sparkle, Users, FileText } from "@phosphor-icons/react";

const EMAIL_TYPES = [
  "Application Received",
  "Interview Invitation",
  "Status Update",
  "Offer Letter",
  "Rejection Letter",
];

const BULK_ACTIONS = ["Send to Interview", "Move to Screening", "Send to Offer", "Reject", "Hire"];

const TABS = [
  { key: "writer", label: "Email Writer", icon: FileText },
  { key: "bulk", label: "Bulk Process", icon: Users },
];

export default function Communication({ statusFilter = "All", locationFilter = "All", requisitionFilter = "All" }) {
  const [tab, setTab] = useState("writer");
  const [candidates, setCandidates] = useState([]);
  const [history, setHistory] = useState([]);

  const load = async () => {
    const [c, comm] = await Promise.all([http.get("/candidates"), http.get("/communications")]);
    setCandidates(c.data);
    setHistory(comm.data);
  };
  useEffect(() => { load(); }, []);

  const visibleCandidates = useMemo(
    () => applyCandidateFilters(candidates, statusFilter, locationFilter, requisitionFilter),
    [candidates, statusFilter, locationFilter, requisitionFilter],
  );
  const visibleIds = useMemo(() => new Set(visibleCandidates.map((c) => c.id)), [visibleCandidates]);
  const visibleHistory = useMemo(
    () => (filtersActive(statusFilter, locationFilter, requisitionFilter)
      ? history.filter((h) => visibleIds.has(h.candidateId))
      : history),
    [history, visibleIds, statusFilter, locationFilter, requisitionFilter],
  );

  return (
    <div className="space-y-5" data-testid="communication-page">
      <div>
        <div className="label-eyebrow">Outreach</div>
        <h1 className="font-display text-3xl font-bold tracking-tight mt-1">Communication</h1>
        <p className="text-slate-500 mt-1 text-sm">
          AI-drafted emails, bulk candidate processing, and outreach history.
        </p>
      </div>

      <div className="card overflow-hidden">
        <div className="tabs-bar px-4">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.key}
                data-testid={`comm-tab-${t.key}`}
                className={`tab-btn flex items-center gap-2 ${tab === t.key ? "active" : ""}`}
                onClick={() => setTab(t.key)}
              >
                <Icon size={14} weight={tab === t.key ? "fill" : "regular"} /> {t.label}
              </button>
            );
          })}
        </div>
        <div className="p-6">
          {filtersActive(statusFilter, locationFilter, requisitionFilter) && (
            <div className="mb-4 text-xs text-[#002FA7] bg-indigo-50 rounded px-3 py-2 flex items-center justify-between" data-testid="comm-filter-active">
              <span>Sidebar filter active — {filterSummary(statusFilter, locationFilter, requisitionFilter)}</span>
              <span className="font-mono">{visibleCandidates.length}/{candidates.length} candidates</span>
            </div>
          )}
          {tab === "writer" && <EmailWriter candidates={visibleCandidates} onSent={load} />}
          {tab === "bulk" && <BulkProcess candidates={visibleCandidates} onDone={load} />}
        </div>
      </div>

      <div className="card overflow-hidden" data-testid="comm-history">
        <div className="px-4 py-3 border-b flex items-center justify-between">
          <h2 className="font-display font-bold">Outreach History</h2>
          {filtersActive(statusFilter, locationFilter, requisitionFilter) && (
            <span className="text-[10px] text-slate-500 font-mono">
              {visibleHistory.length}/{history.length} messages (filtered)
            </span>
          )}
        </div>
        <table className="data-table">
          <thead>
            <tr>
              <th>Sent</th><th>Candidate</th><th>Channel</th><th>Subject</th><th>Status</th>
            </tr>
          </thead>
          <tbody>
            {visibleHistory.length === 0 && (
              <tr><td colSpan={5} className="text-center text-slate-400 py-12">No messages sent yet.</td></tr>
            )}
            {visibleHistory.map((m) => (
              <tr key={m.id}>
                <td className="font-mono text-xs">{new Date(m.sentAt).toLocaleString()}</td>
                <td className="font-medium">{m.candidateName}</td>
                <td>
                  <span className="inline-flex items-center gap-1 text-xs">
                    {m.channel === "Email" ? <Envelope size={12} /> : <ChatText size={12} />} {m.channel}
                  </span>
                </td>
                <td>{m.subject}</td>
                <td><span className="pill pill-offer">{m.status}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function EmailWriter({ candidates, onSent }) {
  const [candidateId, setCandidateId] = useState("");
  const [emailType, setEmailType] = useState("Application Received");
  const [sendImmediate, setSendImmediate] = useState(false);
  const [scheduleDate, setScheduleDate] = useState(new Date().toISOString().slice(0, 10));
  const [extraContext, setExtraContext] = useState("");
  const [draft, setDraft] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!candidateId && candidates[0]) setCandidateId(String(candidates[0].id));
  }, [candidates, candidateId]);

  const cand = candidates.find((c) => String(c.id) === String(candidateId));

  const generate = async () => {
    if (!cand) return;
    setDraft("");
    setStreaming(true);
    await streamSSE("/ai/email-writer", {
      candidateId: cand.id,
      emailType,
      extraContext,
    }, {
      onDelta: (_d, full) => setDraft(full),
      onDone: () => setStreaming(false),
      onError: () => setStreaming(false),
    });
  };

  const parseDraft = () => {
    // Pull subject from "## Subject" section, body from "## Body"
    const subjMatch = draft.match(/## Subject\s*\n+([^\n]+)/);
    const bodyMatch = draft.match(/## Body\s*\n+([\s\S]*)$/);
    return {
      subject: (subjMatch?.[1] || `${emailType} – ${cand?.position}`).trim(),
      body: (bodyMatch?.[1] || draft).trim(),
    };
  };

  const [sendResult, setSendResult] = useState(null);

  const send = async () => {
    if (!cand || !draft) return;
    const { subject, body } = parseDraft();
    setSending(true);
    setSendResult(null);
    try {
      const r = await http.post("/communications", {
        candidateId: cand.id,
        candidateName: cand.name,
        channel: "Email",
        subject,
        body,
      });
      const delivery = r.data?.delivery || {};
      setSendResult({
        ok: !!delivery.sent,
        error: delivery.error,
        to: delivery.to,
        status: r.data?.status,
      });
      if (delivery.sent) {
        setDraft("");
      }
      await onSent();
    } catch (e) {
      setSendResult({ ok: false, error: e.response?.data?.detail || e.message });
    } finally { setSending(false); }
  };

  return (
    <div className="space-y-4" data-testid="tab-email-writer">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <label className="label-eyebrow block mb-1">Select Recipient</label>
          <select
            data-testid="ew-recipient"
            className="input"
            value={candidateId}
            onChange={(e) => setCandidateId(e.target.value)}
          >
            {candidates.map((c) => (
              <option key={c.id} value={c.id}>{c.name} ({c.position})</option>
            ))}
          </select>
        </div>
        <div>
          <label className="label-eyebrow block mb-1">Select Email Type</label>
          <select
            data-testid="ew-type"
            className="input"
            value={emailType}
            onChange={(e) => setEmailType(e.target.value)}
          >
            {EMAIL_TYPES.map((t) => <option key={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <label className="label-eyebrow block mb-1">Or Schedule for Date</label>
          <input
            type="date"
            className="input"
            value={scheduleDate}
            onChange={(e) => setScheduleDate(e.target.value)}
            disabled={sendImmediate}
          />
          <label className="flex items-center gap-2 text-xs text-slate-600 mt-1.5">
            <input
              type="checkbox"
              checked={sendImmediate}
              onChange={(e) => setSendImmediate(e.target.checked)}
            />
            Send Immediately
          </label>
        </div>
      </div>

      {cand && (
        <div className="text-sm text-slate-600 bg-slate-50 border rounded-sm p-3">
          <div>Sending to: <span className="font-semibold">{cand.name}</span></div>
          <div>Email: <span className="font-mono text-[#002FA7]">{cand.email || `${cand.name.toLowerCase().replace(/\s+/g, ".")}@example.com`}</span></div>
        </div>
      )}

      <div>
        <label className="label-eyebrow block mb-1">Optional context for the agent</label>
        <input
          data-testid="ew-context"
          className="input"
          placeholder="e.g. emphasize Saudization, interview slot next Tuesday…"
          value={extraContext}
          onChange={(e) => setExtraContext(e.target.value)}
        />
      </div>

      <div className="flex gap-2">
        <button
          data-testid="ew-ask"
          className="btn btn-primary"
          onClick={generate}
          disabled={streaming || !cand}
        >
          <Sparkle size={14} weight="fill" />
          {streaming ? "Drafting…" : "Ask Email Writer Agent"}
        </button>
        {draft && !streaming && (
          <button data-testid="ew-send" className="btn btn-outline" onClick={send} disabled={sending}>
            <PaperPlaneRight size={14} weight="fill" /> {sending ? "Sending…" : sendImmediate ? "Send Now" : `Schedule for ${scheduleDate}`}
          </button>
        )}
      </div>

      {sendResult && (
        <div
          className={`rounded-md border p-3 text-sm ${sendResult.ok ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-red-300 bg-red-50 text-red-800"}`}
          data-testid="ew-send-result"
        >
          {sendResult.ok ? (
            <>✅ Email delivered to <span className="font-mono">{(sendResult.to || []).join(", ")}</span> via Resend.</>
          ) : (
            <>
              ❌ <strong>Email not sent.</strong> {sendResult.error || "Unknown error"}
              {sendResult.error && sendResult.error.includes("test") && (
                <div className="text-xs mt-1 text-slate-600">
                  Resend&apos;s free/test tier only allows delivery to <strong>verified</strong> recipient addresses. Verify a domain at resend.com to send to anyone, or add the test email in your Resend dashboard.
                </div>
              )}
            </>
          )}
        </div>
      )}

      <div className="border bg-[#fafbfd] rounded-sm p-5 min-h-[260px]">
        {draft ? (
          <>
            <MarkdownLite text={draft} />
            {streaming && <span className="cursor-blink" />}
          </>
        ) : (
          <div className="text-slate-400 text-sm text-center py-10">
            Pick a recipient + email type, then click <em>Ask Email Writer Agent</em>.
          </div>
        )}
      </div>
    </div>
  );
}

function BulkProcess({ candidates, onDone }) {
  const [picked, setPicked] = useState(new Set());
  const [action, setAction] = useState(BULK_ACTIONS[0]);
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState(null);

  const toggle = (id) => setPicked((p) => {
    const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const all = () => setPicked(new Set(candidates.map((c) => c.id)));
  const none = () => setPicked(new Set());

  const run = async () => {
    if (picked.size === 0) return;
    setProcessing(true);
    setResult(null);
    try {
      const r = await http.post("/communications/bulk", {
        candidateIds: Array.from(picked),
        action,
      });
      setResult(r.data);
      setPicked(new Set());
      await onDone();
    } finally { setProcessing(false); }
  };

  return (
    <div className="space-y-4" data-testid="tab-bulk-process">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div className="flex items-end gap-3 flex-wrap">
          <div>
            <label className="label-eyebrow block mb-1">Action</label>
            <select
              data-testid="bp-action"
              className="input"
              value={action}
              onChange={(e) => setAction(e.target.value)}
            >
              {BULK_ACTIONS.map((a) => <option key={a}>{a}</option>)}
            </select>
          </div>
          <button data-testid="bp-run" className="btn btn-primary" onClick={run} disabled={processing || picked.size === 0}>
            {processing ? "Processing…" : `Process ${picked.size} Candidate${picked.size === 1 ? "" : "s"}`}
          </button>
          <button className="btn btn-outline" onClick={all}>Select All</button>
          <button className="btn btn-outline" onClick={none}>Clear</button>
        </div>
        <div className="text-xs text-slate-500">{picked.size} selected of {candidates.length}</div>
      </div>

      {result && (
        <div className="border border-emerald-200 bg-emerald-50 text-emerald-800 text-sm p-3 rounded-sm" data-testid="bp-result">
          ✅ {result.action} applied to {result.processedCount} candidate(s).
        </div>
      )}

      <div className="card overflow-x-auto">
        <table className="data-table" data-testid="bp-table">
          <thead>
            <tr>
              <th style={{ width: 30 }}></th>
              <th>Name</th><th>Position</th><th>Status</th><th>Match</th>
            </tr>
          </thead>
          <tbody>
            {candidates.map((c) => (
              <tr key={c.id}>
                <td>
                  <input
                    type="checkbox"
                    data-testid={`bp-pick-${c.id}`}
                    checked={picked.has(c.id)}
                    onChange={() => toggle(c.id)}
                  />
                </td>
                <td className="font-medium">{c.name}</td>
                <td>{c.position}</td>
                <td><span className={`pill pill-${(c.status || "new").toLowerCase()}`}>{c.status}</span></td>
                <td className="font-mono">{c.matchScore}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
